/* Frequency & Stone — bracelet size selector.

   Two jobs.

   1. The size guide opens as a native <dialog> shown with showModal(), which
      gives the backdrop, Esc, the focus trap and background inertness for
      free. It deliberately does NOT reuse Horizon's <theme-drawer>: that one
      squeezes .page-wrapper, so opening it would reflow the gallery and push
      the buy button while the customer is mid-decision.

   2. Exactly one size reaches the order. S/M/L are radios; a custom
      measurement is a separate hidden input. Only one of them is ever
      submittable, because an order carrying both a standard size and a
      measurement is one someone has to guess at on the bench.

   Bracelets and necklaces share this file. The valid range and the wording of
   the range error come from data attributes, since a wrist and a finished
   necklace length are nothing like the same numbers.
*/
(() => {
  const toCm = (value, unit) => (unit === 'in' ? value * 2.54 : value);
  const round1 = (n) => Math.round(n * 10) / 10;

  const init = (scope = document) => scope.querySelectorAll('[data-fs-size-root]').forEach((root) => {
    const CM_MIN = Number(root.dataset.fsSizeMin) || 10;
    const CM_MAX = Number(root.dataset.fsSizeMax) || 25;
    const MEASURE = root.dataset.fsSizeLabel || 'measurement';
    const dialog     = root.querySelector('[data-fs-size-dialog]');
    const openers    = root.querySelectorAll('[data-fs-size-open]');
    const closers    = root.querySelectorAll('[data-fs-size-close]');
    const radios     = [...root.querySelectorAll('[data-fs-size-radio]')];
    const customChip = root.querySelector('[data-fs-size-custom-chip]');
    const toggle     = root.querySelector('[data-fs-size-custom-toggle]');
    const panel      = root.querySelector('[data-fs-size-custom-panel]');
    const input      = root.querySelector('[data-fs-size-custom-input]');
    const unitBtns   = [...root.querySelectorAll('[data-fs-size-unit]')];
    const hidden     = root.querySelector('[data-fs-size-custom-value]');
    const summary    = root.querySelector('[data-fs-size-custom-summary]');
    const error      = root.querySelector('[data-fs-size-custom-error]');

    let unit = 'cm';
    let lastStandard = radios.find((r) => r.checked) || radios[0];

    // One signal for "the effective size is now X", so a host that is not a
    // product form -- the cart line editor -- can act on the result instead of
    // re-deriving it from the radios and the hidden input, which is the sort of
    // duplicated knowledge that drifts. Declared above the guide-only return
    // below so necklace length, which has no custom wiring at all, still reports.
    const effectiveValue = () => (hidden && !hidden.disabled && hidden.value)
      ? hidden.value
      : (radios.find((r) => r.checked)?.value || '');
    const announceSize = () => root.dispatchEvent(new CustomEvent('fs:size-change', {
      bubbles: true,
      detail: { value: effectiveValue() },
    }));
    root.addEventListener('change', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-fs-size-radio]')) announceSize();
    });

    // ---- dialog ------------------------------------------------------------
    if (dialog) {
      let pushedState = false;
      const open = () => {
        if (dialog.open) return;
        dialog.showModal();
        history.pushState({ fsSizeDialog: true }, '');
        pushedState = true;
      };
      openers.forEach((b) => b.addEventListener('click', open));
      closers.forEach((b) => b.addEventListener('click', () => dialog.close()));
      // Clicking the backdrop closes; the panel stops the click reaching here.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      // Fires for the close button, Esc and the backdrop alike.
      dialog.addEventListener('close', () => {
        if (pushedState) {
          pushedState = false;
          if (history.state && history.state.fsSizeDialog) history.back();
        }
        openers[0]?.focus();
      });
      // Back on mobile should dismiss the sizing layer, not leave the product.
      // If the enlarged illustration is open it owns this step: closing both
      // at once would drop the customer back to the product from two layers
      // deep in one press.
      window.addEventListener('popstate', () => {
        if (!dialog.open) return;
        if (dialog.querySelector('[data-fs-zoom-dialog][open]')) return;
        pushedState = false;
        dialog.close();
      });
    }

    // A selector can be guide-only: necklaces offer three fixed lengths and no
    // custom input, and plain radios already guarantee exactly one value.
    if (!hidden || !input || !customChip || !panel) return;

    // The toggle is optional. On the product page the custom field stays folded
    // away so the buy area reads calmly; inside the cart the whole editor is
    // already behind a disclosure, and a second one nested in the first is a door
    // behind a door. Without a toggle the panel is simply always open.

    // ---- exactly one wrist size -------------------------------------------
    // Custom exists only while it is the chosen size: picking S/M/L removes the
    // chip and folds the panel away, so the row never shows a fourth option
    // that is not in play, or a measurement it is not going to use. The value
    // itself is remembered, and reopening "Need another size?" restores it
    // rather than making the customer type it again.
    let savedValue = '';
    let savedShown = '';

    const collapsePanel = () => {
      if (!toggle) return;
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    const deselectCustom = () => {
      hidden.disabled = true;
      hidden.value = '';
      customChip.classList.remove('is-selected');
      customChip.hidden = true;
      summary.hidden = true;
    };

    const forgetCustom = () => {
      savedValue = '';
      savedShown = '';
      deselectCustom();
      customChip.hidden = true;
    };

    const selectCustom = () => {
      if (!savedValue) return;
      radios.forEach((r) => { r.checked = false; });
      hidden.disabled = false;
      hidden.value = savedValue;
      customChip.hidden = false;
      customChip.classList.add('is-selected');
      summary.textContent = savedValue;
      summary.hidden = false;
      announceSize();
    };

    const selectStandard = (radio) => {
      radio.checked = true;
      lastStandard = radio;
      deselectCustom();
      collapsePanel();
    };

    // Nothing entered, or entered wrongly: fall back to the standard size that
    // was selected before, so the row is never left with no size chosen at all.
    const revertToStandard = () => {
      forgetCustom();
      if (!radios.some((r) => r.checked)) lastStandard.checked = true;
      announceSize();
    };

    const applyCustom = () => {
      const raw = parseFloat(input.value);
      if (!Number.isFinite(raw) || raw <= 0) { revertToStandard(); error.hidden = true; return; }

      const cm = toCm(raw, unit);
      if (cm < CM_MIN || cm > CM_MAX) {
        revertToStandard();
        error.textContent = `Please enter a ${MEASURE} between ${CM_MIN} and ${CM_MAX} cm (${round1(CM_MIN / 2.54)}–${round1(CM_MAX / 2.54)} in).`;
        error.hidden = false;
        return;
      }
      error.hidden = true;

      // Always both units, always metric first, whichever the customer typed in.
      // It used to carry the pair only when they entered inches, so a cm entry
      // reached the bench as a lone number while a standard size arrived with
      // both — the one place a conversion still had to happen by hand. Matching
      // the standard sizes' shape also means the row reads the same either way.
      savedShown = `${round1(cm)} cm / ${round1(cm / 2.54)} in`;
      savedValue = `Custom · ${savedShown}`;
      selectCustom();
    };

    radios.forEach((radio) => radio.addEventListener('change', () => selectStandard(radio)));

    toggle?.addEventListener('click', () => {
      const nowOpen = panel.hidden;
      panel.hidden = !nowOpen;
      toggle.setAttribute('aria-expanded', String(nowOpen));
      if (!nowOpen) return;
      input.focus();
      // Reopening restores the measurement that was entered before, so
      // changing your mind back does not mean typing it again.
      if (input.value) applyCustom();
    });

    input?.addEventListener('input', applyCustom);

    unitBtns.forEach((button) => {
      button.addEventListener('click', () => {
        unit = button.dataset.fsSizeUnit;
        unitBtns.forEach((b) => b.classList.toggle('is-selected', b === button));
        unitBtns.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        applyCustom();
      });
    });


    // Last line of defence: never submit a standard size and a custom one.
    root.closest('form')?.addEventListener('submit', () => {
      if (!hidden.disabled && hidden.value) {
        radios.forEach((r) => { r.checked = false; });
      } else if (!radios.some((r) => r.checked)) {
        selectStandard(lastStandard);
      }
    });

    deselectCustom();

    // Restore what is already saved. A cart line carrying a custom measurement
    // used to open this control with nothing selected and an empty field, so the
    // only way to adjust 14 cm to 14.5 was to type the whole thing again — and a
    // control that shows no current value reads as though the size were lost.
    const preset = root.dataset.fsSizePreset;
    if (preset) {
      const match = radios.find((r) => r.value === preset);
      if (match) {
        match.checked = true;
        lastStandard = match;
      } else if (preset.startsWith('Custom')) {
        const cm = parseFloat(preset.replace(/^Custom[^0-9]*/, ''));
        if (Number.isFinite(cm) && cm > 0) {
          unit = 'cm';
          input.value = String(round1(cm));
          // Rebuilds savedValue and savedShown through the same path a typed
          // measurement takes, so a restored value and a fresh one are identical.
          applyCustom();
        }
      }
    }
  });
  window.FSSizeSelectorInit = init;
  init();
})();

// Pendant-necklace length changes are requests, not a size system. Keep the
// property disabled until the customer deliberately opens the disclosure and
// enters a value, so the default photographed configuration reaches the order
// without duplicated or empty line-item data.
document.querySelectorAll('[data-fs-length-request-toggle]').forEach((toggle) => {
  const root = toggle.closest('.product-size-selector');
  const panel = root?.querySelector('[data-fs-length-request-panel]');
  const input = root?.querySelector('[data-fs-length-request-input]');
  if (!panel || !input) return;
  toggle.addEventListener('click', () => {
    const open = panel.hidden;
    panel.hidden = !open;
    input.disabled = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) input.focus();
  });
  root.closest('form')?.addEventListener('submit', () => {
    input.disabled = !input.value.trim();
    if (input.disabled) return;
    const cm = Number(input.value);
    if (!Number.isFinite(cm) || cm <= 0) {
      input.disabled = true;
      return;
    }
    const round1 = (value) => Math.round(value * 10) / 10;
    input.value = `${round1(cm)} cm / ${round1(cm / 2.54)} in`;
    input.disabled = false;
  });
});

/* Enlarge the sizing illustration. Independent of the size selector above, so
   it works in the PDP dialog and on the full guide page alike. A nested modal
   sits on top of the size panel in the top layer; Esc closes the illustration
   first, then the panel, which is the order a customer expects. */
(() => {
  document.querySelectorAll('[data-fs-zoom-dialog]').forEach((zoom) => {
    const scope = zoom.parentElement;
    const trigger = scope?.querySelector('[data-fs-zoom-trigger]');
    if (!trigger) return;
    let pushed = false;

    const zoomImage = zoom.querySelector('img');

    trigger.addEventListener('click', () => {
      if (zoom.open) return;
      if (zoomImage && !zoomImage.getAttribute('src')) zoomImage.src = zoomImage.dataset.fsZoomSrc;
      zoom.showModal();
      history.pushState({ fsSizeZoom: true }, '');
      pushed = true;
    });

    zoom.querySelector('[data-fs-zoom-close]')?.addEventListener('click', () => zoom.close());
    // Anywhere outside the image closes it; the image itself does not.
    zoom.addEventListener('click', (event) => {
      if (event.target !== zoom.querySelector('img')) zoom.close();
    });
    zoom.addEventListener('close', () => {
      if (pushed) {
        pushed = false;
        if (history.state && history.state.fsSizeZoom) history.back();
      }
      trigger.focus();
    });
    window.addEventListener('popstate', () => {
      if (zoom.open) { pushed = false; zoom.close(); }
    });
  });
})();
